import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye } from 'lucide-react';
import type { SupplierOrg } from '../../types';
import {
  buildSupplierPortalPreviewSearch,
  type SupplierPortalPreviewRole,
} from '../../utils/vendorPortalPreviewQuery';
import { supplierPortalRoleLabel } from '../../utils/supplierWorkFlags';

export interface VendorAdminPageHeaderProps {
  supplierOrgs: SupplierOrg[];
}

/** Toolbar control: open the supplier portal preview as owner or employee. */
const VendorAdminPageHeader: React.FC<VendorAdminPageHeaderProps> = ({ supplierOrgs }) => {
  const navigate = useNavigate();

  const sortedOrgs = [...supplierOrgs]
    .filter(o => o.id)
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

  if (sortedOrgs.length === 0) {
    return (
      <Link to="/materials?tab=price-reviews" className="ops-hide-completed-btn" title="Review agent timber price proposals">
        Materials price reviews
      </Link>
    );
  }

  return (
    <>
      <Link to="/materials?tab=price-reviews" className="ops-hide-completed-btn" title="Review agent timber price proposals">
        Materials price reviews
      </Link>
      <label className="ops-hide-completed-btn vm-view-as-btn">
        <Eye size={13} aria-hidden />
        <select
          className="vm-view-as-select"
          value=""
          aria-label="View as supplier"
          onChange={e => {
            const raw = e.target.value;
            if (!raw) return;
            const [orgId, roleRaw] = raw.split('::');
            if (!orgId) return;
            const role = (roleRaw === 'employee' ? 'employee' : 'owner') as SupplierPortalPreviewRole;
            navigate(`/vendor-portal${buildSupplierPortalPreviewSearch({ orgId, role })}`);
          }}
        >
          <option value="">View as supplier…</option>
          <optgroup label="Owner (quotes & prices)">
            {sortedOrgs.map(org => (
              <option key={`owner-${org.id}`} value={`${org.id}::owner`}>
                {org.name || org.id}
              </option>
            ))}
          </optgroup>
          <optgroup label="Employee (no quote prices)">
            {sortedOrgs.map(org => (
              <option key={`employee-${org.id}`} value={`${org.id}::employee`}>
                {org.name || org.id} · {supplierPortalRoleLabel('employee')}
              </option>
            ))}
          </optgroup>
        </select>
      </label>
    </>
  );
};

export default VendorAdminPageHeader;
